import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/hooks/useAuth";

export interface SelectableColumn {
  key: string;
  label: string;
  /** Always shown; cannot be hidden in the column picker */
  required?: boolean;
  defaultVisible?: boolean;
}

function defaultKeys(columns: SelectableColumn[]) {
  return columns.filter((c) => c.defaultVisible).map((c) => c.key);
}

function readKeys(
  storageKey: string | null,
  columns: SelectableColumn[],
): string[] {
  if (!storageKey) return defaultKeys(columns);
  try {
    const raw = localStorage.getItem(storageKey);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (Array.isArray(parsed)) {
      return parsed.filter((k): k is string => typeof k === "string");
    }
  } catch {
    // ignore
  }
  return defaultKeys(columns);
}

/**
 * Per-user selection of visible table columns, persisted in localStorage
 * under `${name}:${userId}`. `columns` must be a stable (module-level) array.
 */
export function useColumnSelection<C extends SelectableColumn>(
  name: string,
  columns: C[],
) {
  const { user } = useAuth();
  const storageKey = user ? `${name}:${user.id}` : null;

  const [visibleKeys, setVisibleKeys] = useState<string[]>(() =>
    readKeys(storageKey, columns),
  );

  // Re-initialize when user changes
  useEffect(() => {
    setVisibleKeys(readKeys(storageKey, columns));
  }, [storageKey, columns]);

  const persist = useCallback(
    (keys: string[]) => {
      setVisibleKeys(keys);
      if (!storageKey) return;
      try {
        localStorage.setItem(storageKey, JSON.stringify(keys));
      } catch {
        // Storage full or unavailable — silently degrade
      }
    },
    [storageKey],
  );

  const setColumnVisible = useCallback(
    (key: string, visible: boolean) => {
      const without = visibleKeys.filter((k) => k !== key);
      persist(visible ? [...without, key] : without);
    },
    [visibleKeys, persist],
  );

  const resetColumns = useCallback(() => {
    persist(defaultKeys(columns));
  }, [persist, columns]);

  const visibleColumns = useMemo(() => {
    const keys = new Set(visibleKeys);
    return columns.filter((c) => c.required || keys.has(c.key));
  }, [visibleKeys, columns]);

  return { columns, visibleColumns, setColumnVisible, resetColumns };
}
